import React from 'react';
import PropTypes from 'prop-types';

import Checkbox from 'common/components/checkbox';
import DropdownButton from 'common/components/dropdown-button';
import ModalBody from 'common/components/modal-body';
import ModalFooter from 'common/components/modal-footer';
import RadioButton from 'common/components/radio-button';
import SearchBox from 'common/components/search-box';

import {escapeRegex} from 'common/utils/regex-util';
import {triggerQueryParams} from 'common/utils/url-util';
import {nt, t} from 'common/utils/translation-util';

import * as Validators from 'common/validators';

import './index.scss';

export default class DropdownFilter extends React.Component {
  static propTypes = {
    buttonChildren: PropTypes.any,
    buttonProps: PropTypes.object,
    // categorizedFields' keys and values are objects with
    // 'id' and 'displayName' or 'name' properties
    categorizedFields: Validators.mapValidator.isRequired,
    dropdownChildren: PropTypes.any,
    dropdownProps: PropTypes.object,
    initialCategorySelected: PropTypes.object,
    initialFieldsSelected: Validators.setValidator,
    onCancel: PropTypes.func,
    onSave: PropTypes.func.isRequired,
  };

  static defaultProps = {
    buttonChildren: t('Filter'),
    buttonProps: {
      type: 'select',
    },
    dropdownProps: {
      menuPosition: 'down-right',
      menuStyle: {
        width: '416px',
      },
    },
    onCancel: () => {},
    onSave: onSaveDefault,
  };

  static parseCategorizedFields = (options) => {
    // Parses a structure into the categorizedFields
    // map structure, which corresponds categories to
    // their fields.
    // options = [
    // {
    // label: 'Things',
    // fields: [
    // {displayName: 'foo', id: 1, slug: 'f'},
    // {displayName: 'bar', id: 2, slug: 'b'},
    // ],
    // param: 'things',
    // },
    // {
    // label: 'Assets',
    // fields: [
    // {displayName: 'one', id: 1, slug: 'wow'},
    // {displayName: 'two', id: 2, slug: 'totally'},
    // ],
    // param: 'assets',
    // }
    // ]
    const fieldsByCategory = [];
    for (const option of options) {
      if (!option) {
        continue;
      }
      const unique = parseInt(Math.random() * 100000, 10);
      const {fields, label, param, useRadio} = option;
      const category = {
        id: param + String(unique),
        displayName: label,
        slug: param,
        useRadio,
      };
      fields.forEach((field) => (field.slug = param));
      fieldsByCategory.push([category, fields]);
    }
    return new Map(fieldsByCategory);
  };

  static parseInitialFields(options, selected) {
    // Parses a serialized request.GET to
    // return a set of selected field objects.
    // selected = {
    // param1: ['id1', 'id2', 'id3'],
    // param2: ['idx', 'idy', 'idz']
    // }
    const selectedFields = [];

    for (const option of options) {
      if (!option) {
        continue;
      }
      const {fields, param, useRadio} = option;
      const selectedForOption = selected && selected[param];
      if (!selectedForOption) {
        continue;
      }
      const matchingFields = fields.filter(
        ({id}) =>
          selectedForOption.indexOf(String(id)) > -1 ||
          selectedForOption.indexOf(id) > -1,
      );

      if (matchingFields.length) {
        selectedFields.push(...matchingFields);
      } else if (useRadio) {
        selectedFields.push(fields[0]);
      }
    }

    return new Set(selectedFields);
  }

  constructor(props) {
    super(props);
    // ineloquent (but standard) way to get the first key in a map
    const firstCategory = props.categorizedFields.keys().next().value;
    this.state = {
      categorizedFields: props.categorizedFields,
      selectedCategory: props.initialCategorySelected || firstCategory,
      selected: props.initialFieldsSelected || new Set(),
      query: '',
    };
    this.onCancel = this.onCancel.bind(this);
    this.onQueryChange = this.onQueryChange.bind(this);
    this.fieldMatchesQuery = this.fieldMatchesQuery.bind(this);
    this.hasName = this.hasName.bind(this);
    this.removeFilterParam = this.removeFilterParam.bind(this);
    this.findCategorizedField = this.findCategorizedField.bind(this);
  }

  getFilteredCategorizedFields(categorizedFields) {
    return Array.from(categorizedFields, ([category, fields]) => {
      const namedFields = fields.filter(this.hasName);
      return [
        category,
        this.state.query
          ? namedFields.filter(this.fieldMatchesQuery)
          : namedFields,
      ];
    }).filter(([category, fields]) => {
      return category && fields.length;
    });
  }

  getLabelFieldsetMap() {
    const {categorizedFields} = this.state;

    const filtered = this.getFilteredCategorizedFields(categorizedFields);
    if (!filtered.length) {
      return false;
    }

    const filteredCategories = filtered.map((el) => el[0]);
    if (!(filteredCategories.indexOf(this.state.selectedCategory) > -1)) {
      this.setState({
        selectedCategory: filteredCategories[0],
      });
    }

    return new Map(
      filtered.map(([category, fields]) => [
        <div
          className={`DropdownFilter-category ${
            this.state.selectedCategory === category
              ? 'DropdownFilter-category--selected'
              : ''
          }`}
          key={`DropdownFilter-category-${category.id}`}
          onClick={this.onCategorySelect.bind(this, category)}>
          {category.displayName || category.name}
        </div>,
        <fieldset
          className={`DropdownFilter-fieldset ${
            this.state.selectedCategory === category
              ? 'DropdownFilter-fieldset--selected'
              : ''
          }`}
          id={category.id}
          key={`DropdownFilter-fieldset-${category.id}`}>
          <legend className={'DropdownFilter-fieldset-legend'}>
            {category.displayName || category.name}
          </legend>
          {fields.map((field) =>
            category.useRadio ? (
              <RadioButton
                checked={this.state.selected.has(field)}
                key={`DropdownFilter-Radio-${field.id}`}
                label={field.displayName || field.name}
                name={category.slug}
                onChange={this.onFilterSelect.bind(
                  this,
                  field,
                  category.useRadio,
                )}
              />
            ) : (
              <Checkbox
                checked={this.state.selected.has(field)}
                dumb={true}
                key={`DropdownFilter-Checkbox-${field.id}`}
                label={field.displayName || field.name}
                name={field.id.toString()}
                onDeselect={() => this.onFilterDeselect(field)}
                onSelect={() => this.onFilterSelect(field)}
              />
            ),
          )}
        </fieldset>,
      ]),
    );
  }

  findCategorizedField(category, selection) {
    const {categorizedFields} = this.state;
    return categorizedFields
      .values()
      .flatMap((field) => field)
      .find((field) => field.id === selection && field.slug === category);
  }

  removeFilterParam(category, selection) {
    const {selected} = this.state;
    const param = this.findCategorizedField(category, selection);
    selected.delete(param);
    this.setState(
      {
        selected,
      },
      () => {
        this.props.onSave(this.state);
      },
    );
  }

  clearFilters() {
    const selected = new Set();
    this.setState({
      selected,
    });
  }

  fieldMatchesQuery(field) {
    const fieldName = (field.displayName || field.name).toLowerCase();
    const query = this.state.query.toLowerCase();
    const escapedQuery = escapeRegex(query);

    return Boolean(fieldName.match(escapedQuery));
  }

  hasName(field) {
    if (!field.displayName && !field.name) {
      return false;
    }
    return true;
  }

  render() {
    const labelFieldsetMap = this.getLabelFieldsetMap();

    return (
      <span className={'DropdownFilter'}>
        <DropdownButton
          buttonChildren={this.props.buttonChildren}
          buttonProps={this.props.buttonProps}
          dropdownChildren={
            <div>
              <SearchBox
                onQueryChange={this.onQueryChange}
                placeholder={t('Search filters')}
              />
              {labelFieldsetMap ? (
                <div>
                  <ModalBody>
                    <div
                      className={
                        'DropdownFilter-section DropdownFilter-section--left'
                      }>
                      {Array.from(labelFieldsetMap.keys())}
                    </div>
                    <div
                      className={
                        'DropdownFilter-section DropdownFilter-section--right'
                      }>
                      <form>{Array.from(labelFieldsetMap.values())}</form>
                    </div>
                  </ModalBody>
                  <ModalFooter
                    cancelButtonProps={{
                      children: t('Clear all'),
                    }}
                    onCancel={this.onCancel}
                    onSave={this.props.onSave.bind(this, this.state)}
                    saveButtonProps={{
                      tag: 'span',
                      type: 'blue',
                      children: t('Apply'),
                    }}>
                    <span className={'DropdownFilter-num-selected'}>
                      {nt(
                        '%(count)s filter selected',
                        '%(count)s filters selected',
                        this.state.selected.size,
                        {
                          count: this.state.selected.size,
                        },
                      )}
                    </span>
                  </ModalFooter>
                </div>
              ) : (
                <div className={'DropdownFilter-empty-search-message'}>
                  {t('No filters match your search criteria')}
                </div>
              )}
            </div>
          }
          dropdownProps={this.props.dropdownProps}
          lazy={true}
        />
      </span>
    );
  }

  onCancel() {
    this.clearFilters();
    this.props.onCancel();
  }

  onCategorySelect(selectedCategory) {
    this.setState({
      selectedCategory,
    });
  }

  onFilterDeselect(field) {
    const {selected} = this.state;
    selected.delete(field);
    this.setState({
      selected,
    });
  }

  onFilterSelect(field, useRadio) {
    const {selected} = this.state;
    if (useRadio) {
      let categoryField;
      selected.forEach((selectedField) => {
        if (selectedField.slug === field.slug) {
          categoryField = selectedField;
        }
      });
      if (categoryField) {
        selected.delete(categoryField);
      }
    }
    selected.add(field);
    this.setState({
      selected,
    });
  }

  onQueryChange(query) {
    this.setState({
      query,
    });
  }
}

function onSaveDefault(state) {
  const {selected} = state;
  const params = {};

  const {categorizedFields} = this.props;
  const categorySlugs = {};
  for (const key of categorizedFields.keys()) {
    categorySlugs[key.slug] = 1;
  }

  for (const selection of selected.values()) {
    const slug = selection.slug;
    const val = slug in params ? params[slug] : [];
    val.push(selection.id);
    params[slug] = val;
    delete categorySlugs[slug]; // remove seen ones
  }
  // unselected categories should be removed from query string
  const toRemove = Object.keys(categorySlugs);
  triggerQueryParams(params, toRemove);
}
